import React, { useRef, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { API, formatErr } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  FileXls, FilePdf, Download, Sparkle, TrashSimple, CheckCircle, Warning,
} from "@phosphor-icons/react";
import { titleCaseTR } from "@/lib/textFormat";

const UNITS = ["adet", "kg", "ton", "m", "m²", "m³", "lt", "torba", "paket", "palet"];

// Excel/PDF veri temizleme yardımcıları
const _norm = (s) => (s || "").toString().trim().toLowerCase()
  .replace(/ı/g, "i").replace(/ç/g, "c").replace(/ğ/g, "g")
  .replace(/ö/g, "o").replace(/ş/g, "s").replace(/ü/g, "u");

// Excel'i parçalayıp kalem dizisi oluştur.
// Başlık satırını akıllı tespit et; başlık yoksa Sütun A=isim, B=miktar, C=birim varsay.
function _parseSheet(rows) {
  if (!rows || rows.length === 0) return [];
  // Boş olmayan ilk satırlardan (ilk 5) başlığı bul
  let headerIdx = -1;
  let colMap = { name: -1, qty: -1, unit: -1, cat: -1 };
  const NAME_HINTS = ["malzeme", "urun", "ürün", "acikla", "aciklama", "aç", "name", "material", "kalem"];
  const QTY_HINTS = ["miktar", "adet", "quantity", "qty"];
  const UNIT_HINTS = ["birim", "unit", "olcu", "ölçü"];
  const CAT_HINTS = ["kategori", "category", "grup", "tip"];

  for (let r = 0; r < Math.min(rows.length, 8); r++) {
    const row = rows[r] || [];
    const norms = row.map(_norm);
    const nIdx = norms.findIndex(v => v && NAME_HINTS.some(h => v.includes(h)));
    const qIdx = norms.findIndex(v => v && QTY_HINTS.some(h => v.includes(h)));
    if (nIdx >= 0 && qIdx >= 0) {
      headerIdx = r;
      colMap.name = nIdx;
      colMap.qty = qIdx;
      colMap.unit = norms.findIndex(v => v && UNIT_HINTS.some(h => v.includes(h)));
      colMap.cat = norms.findIndex(v => v && CAT_HINTS.some(h => v.includes(h)));
      break;
    }
  }
  const items = [];
  if (headerIdx >= 0) {
    for (let r = headerIdx + 1; r < rows.length; r++) {
      const row = rows[r] || [];
      const nm = (row[colMap.name] || "").toString().trim();
      if (!nm) continue;
      const qtyRaw = row[colMap.qty];
      const qty = typeof qtyRaw === "number" ? qtyRaw : parseFloat(String(qtyRaw || "").replace(",", "."));
      if (isNaN(qty)) continue;
      let unit = colMap.unit >= 0 ? String(row[colMap.unit] || "adet").trim().toLowerCase() : "adet";
      unit = unit === "m2" ? "m²" : unit === "m3" ? "m³" : unit;
      if (!UNITS.includes(unit)) unit = "adet";
      const category = colMap.cat >= 0 ? String(row[colMap.cat] || "").trim() : "";
      items.push({ material_name: titleCaseTR(nm), quantity: qty, unit, category });
    }
  } else {
    // Başlık yok → A/B/C kolonları
    for (let r = 0; r < rows.length; r++) {
      const row = rows[r] || [];
      const nm = (row[0] || "").toString().trim();
      if (!nm) continue;
      const qtyRaw = row[1];
      const qty = typeof qtyRaw === "number" ? qtyRaw : parseFloat(String(qtyRaw || "").replace(",", "."));
      if (isNaN(qty)) continue;
      let unit = String(row[2] || "adet").trim().toLowerCase();
      unit = unit === "m2" ? "m²" : unit === "m3" ? "m³" : unit;
      if (!UNITS.includes(unit)) unit = "adet";
      items.push({ material_name: titleCaseTR(nm), quantity: qty, unit, category: "" });
    }
  }
  return items;
}

/**
 * Malzeme kalemleri toplu içe aktarma dialog'u.
 * Excel (yerelde xlsx) veya PDF/foto (backend + Gemini) desteği.
 *
 * Props:
 *  - open, onOpenChange
 *  - onImport: (items: [{material_name, quantity, unit, category?}]) => void
 *    Kullanıcı onayladığında seçili kalemleri parent'a iletir.
 */
export default function ItemsImportDialog({ open, onOpenChange, onImport }) {
  const excelInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const [items, setItems] = useState([]);   // [{material_name, quantity, unit, category, _selected}]
  const [source, setSource] = useState(""); // "excel" | "pdf"
  const [busy, setBusy] = useState(false);
  const [warning, setWarning] = useState("");

  const reset = () => { setItems([]); setSource(""); setWarning(""); };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const handleExcelFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // input reset
    if (!file) return;
    setBusy(true); setWarning("");
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sh = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sh, { header: 1, defval: "" });
      const parsed = _parseSheet(rows);
      if (parsed.length === 0) {
        setWarning("Excel dosyasında geçerli kalem bulunamadı. Sütunları kontrol edin: Malzeme Adı, Miktar, Birim (opsiyonel), Kategori (opsiyonel).");
      } else {
        toast.success(`${parsed.length} kalem okundu`);
      }
      setItems(parsed.map(x => ({ ...x, _selected: true })));
      setSource("excel");
    } catch (err) {
      toast.error("Excel okunamadı: " + (err.message || err));
    } finally { setBusy(false); }
  };

  const handlePdfFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true); setWarning("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/rfqs/parse-items`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const list = r.data?.items || [];
      if (list.length === 0) {
        setWarning("AI hiç kalem tespit edemedi. Daha net bir görsel/tarama deneyin.");
      } else {
        toast.success(`AI ${list.length} kalem tespit etti (${r.data?.pages_processed || 1} sayfa)`);
      }
      setItems(list.map(x => ({ ...x, _selected: true })));
      setSource("pdf");
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const data = [
      ["Malzeme Adı", "Miktar", "Birim", "Kategori"],
      ["Akrilik Cephe Boyası", 200, "kg", "Boya"],
      ["Çimento CEM II 42.5R", 500, "torba", "Çimento"],
      ["Nervürlü Demir Çubuk 12mm", 2000, "kg", "Demir"],
    ];
    const ws = XLSX.utils.aoa_to_sheet(data);
    ws["!cols"] = [{ wch: 35 }, { wch: 10 }, { wch: 8 }, { wch: 18 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Kalemler");
    XLSX.writeFile(wb, "malzeme-kalemleri-sablon.xlsx");
  };

  const selectAll = (v) => setItems(prev => prev.map(x => ({ ...x, _selected: v })));
  const toggleOne = (i) => setItems(prev => prev.map((x, j) => j === i ? { ...x, _selected: !x._selected } : x));
  const updateItem = (i, patch) => setItems(prev => prev.map((x, j) => j === i ? { ...x, ...patch } : x));
  const removeItem = (i) => setItems(prev => prev.filter((_, j) => j !== i));

  const doImport = () => {
    const chosen = items
      .filter(x => x._selected && x.material_name && parseFloat(x.quantity) > 0)
      .map(({ _selected, ...rest }) => rest);
    if (chosen.length === 0) {
      toast.error("En az bir kalem seçmelisiniz");
      return;
    }
    onImport(chosen);
    reset();
    onOpenChange(false);
    toast.success(`${chosen.length} kalem eklendi`);
  };

  const selectedCount = items.filter(x => x._selected).length;
  const allSelected = items.length > 0 && selectedCount === items.length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="rounded-none sm:rounded-sm max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-bold tracking-tight font-heading">
            Kalem İçe Aktar
          </DialogTitle>
        </DialogHeader>

        {items.length === 0 ? (
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Excel */}
              <div className="p-6 border-2 border-dashed border-slate-300 hover:border-[#95C11F] hover:bg-[#F7FBE9]/40 transition-colors">
                <div className="flex items-start gap-3 mb-3">
                  <FileXls size={28} weight="duotone" className="text-[#4E7A0F] shrink-0" />
                  <div>
                    <div className="font-bold text-slate-800 text-sm">Excel'den İçe Aktar</div>
                    <div className="text-xs text-slate-500 mt-1">
                      .xlsx / .xls dosyasından kalemleri otomatik alır.
                      Kolonlar: <strong>Malzeme Adı</strong>, <strong>Miktar</strong>,
                      Birim (opsiyonel), Kategori (opsiyonel).
                    </div>
                  </div>
                </div>
                <input ref={excelInputRef} type="file" accept=".xlsx,.xls"
                       className="hidden" onChange={handleExcelFile}
                       data-testid="import-excel-file" />
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button onClick={() => excelInputRef.current?.click()} disabled={busy}
                          data-testid="import-excel-btn"
                          className="rounded-none sm:rounded-sm bg-[#4E7A0F] hover:bg-[#3d6109] text-white text-xs uppercase tracking-widest font-bold flex-1">
                    <FileXls size={14} className="mr-1.5" />
                    {busy ? "Okunuyor..." : "Excel Seç"}
                  </Button>
                  <Button variant="outline" size="sm" onClick={downloadTemplate}
                          data-testid="import-excel-template"
                          className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest font-bold">
                    <Download size={14} className="mr-1.5" />
                    Şablon İndir
                  </Button>
                </div>
              </div>

              {/* PDF / Foto */}
              <div className="p-6 border-2 border-dashed border-slate-300 hover:border-[#1B4776] hover:bg-slate-50 transition-colors">
                <div className="flex items-start gap-3 mb-3">
                  <FilePdf size={28} weight="duotone" className="text-red-600 shrink-0" />
                  <div>
                    <div className="font-bold text-slate-800 text-sm flex items-center gap-1.5">
                      PDF / Fotoğraftan İçe Aktar
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#F7FBE9] text-[#4E7A0F] text-[9px] font-bold uppercase tracking-widest">
                        <Sparkle size={8} weight="fill" /> AI
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Metraj tablosu, malzeme listesi veya el yazması listeleri
                      Gemini görsel AI ile okur. Birden fazla sayfa desteklenir
                      (ilk 3 sayfa).
                    </div>
                  </div>
                </div>
                <input ref={pdfInputRef} type="file"
                       accept="application/pdf,image/*"
                       className="hidden" onChange={handlePdfFile}
                       data-testid="import-pdf-file" />
                <Button onClick={() => pdfInputRef.current?.click()} disabled={busy}
                        data-testid="import-pdf-btn"
                        className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white text-xs uppercase tracking-widest font-bold w-full">
                  <FilePdf size={14} className="mr-1.5" />
                  {busy ? "AI Analiz Ediyor..." : "PDF / Foto Seç"}
                </Button>
              </div>
            </div>
            {warning && (
              <div className="p-3 bg-amber-50 border border-amber-300 text-amber-900 text-xs flex items-start gap-2">
                <Warning size={14} weight="fill" className="mt-0.5 shrink-0" />
                <span>{warning}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col">
            <div className="flex flex-wrap items-center gap-3 mb-3 pb-3 border-b border-slate-200">
              <div className="text-xs uppercase tracking-widest font-bold text-slate-600">
                Önizleme · <span className="text-[#1B4776]">{selectedCount}</span> / {items.length} seçili
              </div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                Kaynak: {source === "excel" ? "Excel" : "PDF/Foto (AI)"}
              </div>
              <div className="ml-auto flex gap-2">
                <Button variant="outline" size="sm" onClick={reset}
                        data-testid="import-clear"
                        className="rounded-none sm:rounded-sm text-xs">
                  Sıfırla
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto pr-1">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-slate-200">
                    <th className="text-left py-2 pl-1 w-8">
                      <Checkbox checked={allSelected} onCheckedChange={selectAll}
                                data-testid="import-select-all" />
                    </th>
                    <th className="text-left py-2 px-1 font-bold">Malzeme</th>
                    <th className="text-right py-2 px-1 font-bold w-24">Miktar</th>
                    <th className="text-left py-2 px-1 font-bold w-24">Birim</th>
                    <th className="text-left py-2 px-1 font-bold w-32">Kategori</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={i}
                        data-testid={`import-row-${i}`}
                        className={`border-b border-slate-100 ${!it._selected ? "opacity-40" : ""}`}>
                      <td className="py-1 pl-1">
                        <Checkbox checked={!!it._selected}
                                  onCheckedChange={() => toggleOne(i)}
                                  data-testid={`import-check-${i}`} />
                      </td>
                      <td className="py-1 px-1">
                        <Input value={it.material_name}
                               onChange={(e) => updateItem(i, { material_name: e.target.value })}
                               data-testid={`import-name-${i}`}
                               className="rounded-none sm:rounded-sm h-8 text-sm" />
                      </td>
                      <td className="py-1 px-1">
                        <Input type="number" step="0.01" value={it.quantity}
                               onChange={(e) => updateItem(i, { quantity: e.target.value })}
                               data-testid={`import-qty-${i}`}
                               className="rounded-none sm:rounded-sm h-8 text-right font-mono text-sm" />
                      </td>
                      <td className="py-1 px-1">
                        <Select value={it.unit || "adet"}
                                onValueChange={(v) => updateItem(i, { unit: v })}>
                          <SelectTrigger className="rounded-none sm:rounded-sm h-8 text-xs" data-testid={`import-unit-${i}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-1 px-1">
                        <Input value={it.category || ""}
                               onChange={(e) => updateItem(i, { category: e.target.value })}
                               data-testid={`import-cat-${i}`}
                               placeholder="—"
                               className="rounded-none sm:rounded-sm h-8 text-xs" />
                      </td>
                      <td className="py-1 pr-1 text-right">
                        <button onClick={() => removeItem(i)}
                                data-testid={`import-remove-${i}`}
                                className="w-7 h-7 flex items-center justify-center hover:bg-red-50 text-red-600">
                          <TrashSimple size={12} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter>
          {items.length > 0 && (
            <>
              <Button variant="outline" onClick={() => handleClose(false)}
                      data-testid="import-cancel"
                      className="rounded-none sm:rounded-sm">
                İptal
              </Button>
              <Button onClick={doImport} disabled={selectedCount === 0}
                      data-testid="import-confirm"
                      className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
                <CheckCircle size={14} className="mr-1.5" weight="fill" />
                {selectedCount} Kalemi Ekle
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
